import React, { useEffect, useState } from 'react';
import { useToast } from 'src/context/ToastProvider';
import { updatePositionExpiryById } from 'src/apis/position';
import { useMutation, useQueryClient } from 'react-query';
import { get } from 'lodash';
import { Drawer } from '../shared/Drawer';
import { useMediaQuery } from '@mui/material';
import { Button, Label, Spinner } from 'flowbite-react';
import { UpdatePositionExpiryByIdInput } from 'types';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import dayjs from 'dayjs';

const UpdatePositionExpiryModal = ({
	isOpen,
	onClose,
	positionId,
	currentExpiryDate,
}: {
	isOpen: boolean;
	positionId: string;
	onClose: () => void;
	currentExpiryDate: Date;
}) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const isMobile = useMediaQuery('(max-width:960px)');
	const [positionExpiryInput, setPositionExpiryInput] = useState<UpdatePositionExpiryByIdInput>({
		positionId: positionId || '',
		expiresAt: new Date(),
	});

	useEffect(() => {
		setPositionExpiryInput({
			positionId: positionId || '',
			expiresAt: new Date(),
		});
	}, []);

	const updatePositionExpiryByIdFn = useMutation(['updatePositionExpiryById'], updatePositionExpiryById, {
		onSuccess(data) {
			queryClient.refetchQueries(['getMyRegisteredPositions']);
			queryClient.refetchQueries(['getPositionById', positionId]);
			toast({
				title: 'Position expiry updated succssfully',
				duration: 5000,
				type: 'success',
			});
			onClose && onClose();
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: errorMessage,
				duration: 5000,
				type: 'error',
			});
		},
	});

	return (
		<Drawer
			isOpen={isOpen}
			minHeight="fit-content"
			isModal={!isMobile}
			onClose={() => {
				onClose && onClose();
			}}
			header={<h1 className="px-4 pt-3 text-xl font-inter font-semibold">Update Position Expiry</h1>}
			body={
				<form
					id="updatePositionExpiry"
					onSubmit={e => {
						e.preventDefault();
						console.log('positionExpiryInput:', positionExpiryInput);
						updatePositionExpiryByIdFn.mutate(positionExpiryInput);
					}}
				>
					<div className="p-2 mt-2 flex flex-col gap-2">
						<div className="mt-[16px]">
							<div className="mb-[8px]">
								<Label>{'Expiry Date '}</Label>
								<span className="text-[red]">*</span>
							</div>

							<MobileDateTimePicker
								value={dayjs(currentExpiryDate || positionExpiryInput?.expiresAt)}
								format="DD MMM, YYYY, hh:mm A"
								minDateTime={dayjs()}
								onChange={date => {
									const dateValue = date as { $d?: Date };
									setPositionExpiryInput({
										positionId: positionId,
										expiresAt: dateValue?.$d || new Date(),
									});
								}}
								sx={{
									'&': {
										width: '100%',
									},
									'& .MuiInputBase-input': {
										padding: '10px',
										borderRadius: '8px',
										backgroundColor: 'white',
										border: '1px solid rgb(209 213 219 / 1)',
										fontSize: '14px',
									},
									'& .MuiInputBase-root': {
										outline: 'none',
										padding: '0px',
										'&:hover': {
											outline: 'none',
										},
									},
									'& fieldset': {
										display: 'none',
									},
								}}
							/>
						</div>
					</div>
				</form>
			}
			footer={
				<div className="dark:bg-[#111E2C] sticky bottom-0 py-3 px-[16px] rounded-b-md">
					<Button
						type="submit"
						form="updatePositionExpiry"
						color="purplePlaced"
						className="w-full"
						disabled={
							!positionExpiryInput?.positionId ||
							!positionExpiryInput?.expiresAt ||
							updatePositionExpiryByIdFn.isLoading
						}
					>
						{updatePositionExpiryByIdFn.isLoading ? <Spinner /> : 'Update'}
					</Button>
				</div>
			}
		/>
	);
};

export default UpdatePositionExpiryModal;
